import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import type { Eu } from '../../acesso';
import { lerAvisosLidos, marcarAvisoLido } from '../../avisos-lidos';
import { jaLeu } from './AvisoCritico';
import './tour.css';

/**
 * TUTORIAL DE BOAS-VINDAS DO INOVAPAY 2.0 (pedido do dono, 27/09/2026). Na primeira vez que a
 * pessoa entra no 2.0, o Inô (o robozinho do InovaPay) passeia com ela pelo app: acende cada botão
 * do menu e cada canto que importa (vender, minhas vendas, premiação com saldo e devoluções,
 * ticket médio, Giro, conta), navegando de verdade pelas telas.
 *
 * Visto = guardado no SERVIDOR por pessoa (dono, 28/09: uma vez só, em qualquer aparelho), com
 * cópia no navegador. Rever: Conta → "Ver o tutorial de
 * novo" (dispara `inovapay:tour`). Sem o alvo na tela (carregando, tela estreita), o balão aparece
 * no meio — nunca trava.
 */

interface Passo {
  /** Tela para onde ir antes de mostrar o passo. */
  readonly rota?: string;
  /** `data-tour` do elemento que acende. */
  readonly alvo?: string;
  readonly titulo: string;
  readonly texto: string;
}

const PASSOS = (primeiro: string, ocultos: boolean): readonly Passo[] =>
  passosSemValores(ocultos, TODOS_OS_PASSOS(primeiro));

/** "Só números": sem o cartão de premiação e sem falar de R$ nos passos que ficam. */
function passosSemValores(ocultos: boolean, passos: readonly Passo[]): readonly Passo[] {
  if (!ocultos) return passos;
  return passos
    .filter((p) => p.alvo !== 'premiacao')
    .map((p) =>
      p.alvo === 'ticket'
        ? {
            ...p,
            titulo: 'Seus cupons',
            texto:
              'Aqui ficam os itens por cupom e os produtos diferentes por cupom. Suba os seus!',
          }
        : p.alvo === 'minha-premiacao'
          ? {
              ...p,
              texto: 'A meta da semana, os itens confirmados e o que falta para bater a meta.',
            }
          : p,
    );
}

const TODOS_OS_PASSOS = (primeiro: string): readonly Passo[] => [
  {
    rota: 'inicio',
    titulo: `Oi, ${primeiro}! Eu sou o Inô 👋`,
    texto:
      'Bem-vindo(a) ao InovaPay 2.0, a nova versão do InovaPay! Vou te mostrar onde fica cada coisa. Leva só 1 minutinho.',
  },
  {
    rota: 'inicio',
    alvo: 'nav-inicio',
    titulo: 'Início',
    texto:
      'Aqui é a sua página inicial: a meta da semana, os avisos da gerência, o seu bloco do Giro de hoje e o que está pagando mais.',
  },
  {
    rota: 'inicio',
    alvo: 'premiacao',
    titulo: 'Sua premiação da semana',
    texto:
      'Este é o quanto você já lançou de premiação na semana. Toque aqui para abrir a Minha premiação.',
  },
  {
    rota: 'inicio',
    alvo: 'nav-vendas',
    titulo: 'Vender',
    texto:
      'É por aqui que você lança a venda de vitamina, pré-vencido e paradão. Busque pelo nome ou pelo código, ou leia o código de barras com a câmera.',
  },
  {
    rota: 'desempenho',
    alvo: 'nav-desempenho',
    titulo: 'Meu desempenho',
    texto:
      'Seus números: posição, metas do mês e as suas médias. Só os seus números: ninguém vê os seus e você não vê os dos colegas.',
  },
  {
    rota: 'desempenho',
    alvo: 'ticket',
    titulo: 'Seu ticket médio',
    texto:
      'O ticket médio é o valor médio de cada cupom seu no HOS. Aqui também ficam os itens por cupom e os produtos diferentes por cupom. Suba o seu!',
  },
  {
    rota: 'desempenho',
    alvo: 'minhas-vendas',
    titulo: 'Minhas vendas',
    texto:
      'Suas últimas vendas lançadas, com a situação de cada uma. Errou? Dá para cancelar a própria venda em até 24 horas.',
  },
  {
    rota: 'desempenho',
    alvo: 'minha-premiacao',
    titulo: 'Minha premiação',
    texto:
      'O saldo da semana, de onde veio cada real, o que foi descontado (devoluções e cancelamentos) e o seu recibo.',
  },
  {
    rota: 'desempenho',
    alvo: 'nav-giro',
    titulo: 'Giro de Prateleira',
    texto:
      'Todo dia você recebe um bloco da prateleira. Toque em "Bloco recebido", conte os produtos e reponha.',
  },
  {
    rota: 'desempenho',
    alvo: 'nav-conta',
    titulo: 'Conta',
    texto:
      'Sua foto, sua senha e os seus dados. O tutorial também fica aqui, se quiser ver de novo.',
  },
  {
    rota: 'inicio',
    titulo: 'Pronto! 🎉',
    texto: 'Agora é com você. Boas vendas!',
  },
];

const chave = (login: string) => `inovapay2.tour.v1.${login}`;
function jaViu(login: string): boolean {
  try {
    return localStorage.getItem(chave(login)) === 'visto';
  } catch {
    return false;
  }
}
function guardarVisto(login: string) {
  try {
    localStorage.setItem(chave(login), 'visto');
  } catch {
    /* sem armazenamento (aba anônima): o servidor lembra */
  }
}
function marcarVisto(login: string) {
  guardarVisto(login);
  void marcarAvisoLido('tour-boas-vindas-v1');
}

/** O elemento visível com aquele `data-tour` (menu de baixo no celular, trilho no PC). */
function alvoVisivel(nome: string): HTMLElement | null {
  for (const el of document.querySelectorAll<HTMLElement>(`[data-tour="${nome}"]`)) {
    const r = el.getBoundingClientRect();
    if (r.width > 0 && r.height > 0) return el;
  }
  return null;
}

/** Abrir o tutorial de qualquer lugar (Conta → "Ver o tutorial de novo"). */
export const abrirTutorial = () => window.dispatchEvent(new Event('inovapay:tour'));

export function TourDeBoasVindas({ eu }: { eu: Eu | null }) {
  const [passo, setPasso] = useState<number | null>(null);
  const [caixa, setCaixa] = useState<DOMRect | null>(null);
  const nome = eu ? eu.nomeExibicao || eu.nome : '';
  const primeiroNome = nome.split(' ')[0] ?? '';
  // a lista é a mesma entre desenhos: o passo atual só muda quando o PASSO muda (senão a busca do
  // alvo recomeçava a cada desenho e a luz nunca acendia)
  const passos = useMemo(
    () => PASSOS(primeiroNome, eu?.valoresOcultos === true),
    [primeiroNome, eu?.valoresOcultos],
  );

  useEffect(() => {
    // primeira vez: depois do Aviso crítico (os dois juntos, um por cima do outro, ninguém lê)
    let vivo = true;
    const primeiraVez = () => {
      if (!eu || !jaLeu(eu.login)) return;
      // viu neste aparelho (inclusive antes de 28/09): garante que o servidor também sabe
      if (jaViu(eu.login)) {
        void marcarAvisoLido('tour-boas-vindas-v1');
        return;
      }
      const login = eu.login;
      void lerAvisosLidos().then((lidos) => {
        if (!vivo) return;
        if (lidos?.includes('tour-boas-vindas-v1')) guardarVisto(login);
        else setPasso(0);
      });
    };
    primeiraVez();
    const abrir = () => setPasso(0);
    window.addEventListener('inovapay:tour', abrir);
    window.addEventListener('inovapay:aviso-lido', primeiraVez);
    return () => {
      vivo = false;
      window.removeEventListener('inovapay:tour', abrir);
      window.removeEventListener('inovapay:aviso-lido', primeiraVez);
    };
  }, [eu]);

  const atual = passo === null ? null : passos[passo];

  // vai para a tela do passo e procura o alvo (a tela pode estar carregando: tenta por 1,5 s)
  useLayoutEffect(() => {
    if (!atual) return;
    if (atual.rota && window.location.hash.replace(/^#\/?/, '').split('?')[0] !== atual.rota)
      window.location.hash = `#/${atual.rota}`;
    setCaixa(null);
    if (!atual.alvo) return;
    let tentativas = 0;
    let parar = false;
    const procurar = () => {
      if (parar) return;
      const el = alvoVisivel(atual.alvo ?? '');
      if (el) {
        el.scrollIntoView({ block: 'center', behavior: 'smooth' });
        window.setTimeout(() => !parar && setCaixa(el.getBoundingClientRect()), 350);
        return;
      }
      if (++tentativas < 15) window.setTimeout(procurar, 100);
    };
    procurar();
    const medir = () => {
      const el = alvoVisivel(atual.alvo ?? '');
      if (el) setCaixa(el.getBoundingClientRect());
    };
    window.addEventListener('resize', medir);
    window.addEventListener('scroll', medir, true);
    return () => {
      parar = true;
      window.removeEventListener('resize', medir);
      window.removeEventListener('scroll', medir, true);
    };
  }, [atual]);

  const fechar = useCallback(() => {
    if (eu) marcarVisto(eu.login);
    setPasso(null);
    window.location.hash = '#/inicio';
  }, [eu]);

  useEffect(() => {
    if (passo === null) return;
    const tecla = (e: KeyboardEvent) => {
      if (e.key === 'Escape') fechar();
    };
    window.addEventListener('keydown', tecla);
    return () => window.removeEventListener('keydown', tecla);
  }, [passo, fechar]);

  if (passo === null || !atual) return null;
  const ultimo = passo === passos.length - 1;
  const folga = 6;
  // balão embaixo do alvo quando o alvo está na metade de cima; em cima, quando está embaixo
  const emCima = caixa ? caixa.top > window.innerHeight / 2 : false;
  const estiloDoBalao: React.CSSProperties = caixa
    ? emCima
      ? { bottom: window.innerHeight - caixa.top + 14 }
      : { top: caixa.bottom + 14 }
    : {};

  return (
    <div className="tour" role="dialog" aria-modal="true" aria-label="Tutorial do InovaPay 2.0">
      {caixa ? (
        <div
          className="tour-luz"
          style={{
            top: caixa.top - folga,
            left: caixa.left - folga,
            width: caixa.width + folga * 2,
            height: caixa.height + folga * 2,
          }}
        />
      ) : (
        <div className="tour-fundo" />
      )}
      <div className={`tour-balao${caixa ? '' : ' tour-balao-meio'}`} style={estiloDoBalao}>
        <div className="tour-robo" aria-hidden="true">
          <svg viewBox="0 0 64 64" width="56" height="56">
            <line
              x1="32"
              y1="4"
              x2="32"
              y2="13"
              stroke="currentColor"
              strokeWidth="3"
              strokeLinecap="round"
            />
            <circle cx="32" cy="5" r="4" fill="var(--brand-500)" />
            <rect x="10" y="13" width="44" height="36" rx="12" fill="currentColor" />
            <rect x="16" y="20" width="32" height="20" rx="8" fill="#fff" />
            <circle className="tour-olho" cx="25" cy="30" r="3.6" fill="var(--ink-900)" />
            <circle className="tour-olho" cx="39" cy="30" r="3.6" fill="var(--ink-900)" />
            <path
              d="M24 44 q8 5 16 0"
              stroke="#fff"
              strokeWidth="3"
              fill="none"
              strokeLinecap="round"
            />
            <rect x="4" y="25" width="6" height="12" rx="3" fill="currentColor" />
            <rect x="54" y="25" width="6" height="12" rx="3" fill="currentColor" />
          </svg>
        </div>
        <div className="tour-conteudo">
          <div className="tour-titulo">{atual.titulo}</div>
          <p>{atual.texto}</p>
          <div className="tour-rodape">
            <span className="tour-contador">
              {passo + 1} de {passos.length}
            </span>
            <div className="tour-botoes">
              {!ultimo && (
                <button type="button" className="tour-pular" onClick={fechar}>
                  Pular
                </button>
              )}
              {passo > 0 && !ultimo && (
                <button type="button" className="tour-voltar" onClick={() => setPasso(passo - 1)}>
                  Voltar
                </button>
              )}
              <button
                type="button"
                className="tour-seguir"
                autoFocus
                onClick={() => (ultimo ? fechar() : setPasso(passo + 1))}
              >
                {passo === 0 ? 'Começar' : ultimo ? 'Começar a usar' : 'Próximo'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
